import React, { useContext } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { UsuarioContext } from './personaContext';
import useIntervencionesDisponibilidad from '../componentes/intervenciones/hooks/useIntervencionesDisponibilidad';
import { guardarDestinoRetorno, destinoDesdeLocationState } from '../../funciones/rutaRetorno';

// Aviso que se pasa por el estado de navegación cuando el guard manda al panel
// a un usuario autenticado sin permiso, para que /panel-inicio lo muestre.
export const AVISO_SIN_ACCESO = 'No tienes acceso a esa sección.';

// Spinner compartido (mismo patrón anti-parpadeo usado en el guard).
const LoaderSpinner = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
        </div>
    </div>
);


const ProtectedRoute = ({ allowedRoles, allowSupervisors = false, allowGestionVacaciones = false, requiredPermission = null, requiredPermissionPrefix = null, requiredModule = null, allowIfParticipante = false, allowIfTecnico = false }) => {
    const { persona, permisos, permisosCargados, cargando, modulos, modulosCargados, ambito, ambitoCargado, ambitoError } = useContext(UsuarioContext);
    const location = useLocation();

    if (cargando) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh'
            }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    const userRole = persona?.rol?.toLowerCase();
    const isSupervisor = persona?.es_supervisor || false;

    /* ── EL INTERRUPTOR DE MÓDULO VA ANTES QUE TODO, SUPER ADMIN INCLUIDO ──────────────────
     *
     * Aquí está la razón de que ocultar por permisos no ocultara nada: tres líneas distintas
     * dejan pasar a Super Administrador sin mirar ningún permiso —ésta, la del middleware del
     * backend y la del menú—. Con cero filas en `permiso_roles`, las cuentas de ese rol seguían
     * viendo el módulo entero.
     *
     * Los permisos dicen «tú no». El interruptor dice «esto no está». Y si no está, no está para
     * nadie: por eso este bloque va ARRIBA, antes del bypass y antes de cualquier `allowedRoles`.
     *
     * Y se espera a `modulosCargados`, NO a `permisosCargados`: ese segundo se pone a `true`
     * también cuando todavía no hay persona —«sin persona, nada que esperar»—, y en ese instante
     * el mapa está vacío. Decidir ahí redirigía en el primer render, antes de que nadie hubiera
     * preguntado nada. **Vacío y «todavía no lo sé» no son lo mismo.**
     */
    if (requiredModule) {
        // Sólo se espera si HAY sesión: sin persona, el gate de más abajo manda al login, y
        // quedarse girando aquí lo dejaría en una página en blanco.
        if (persona && !modulosCargados) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            );
        }
        if ((modulos || {})[requiredModule] !== true) {
            return <Navigate to="/gestionpersonas/personas" replace state={{ from: location }} />;
        }
    }

    // Super Admin pasa siempre
    if (userRole === 'super administrador') {
        return <Outlet />;
    }

    if (allowedRoles && allowedRoles.includes(userRole)) {
        return <Outlet />;
    }

    if (allowSupervisors && isSupervisor) {
        return <Outlet />;
    }

    if (allowGestionVacaciones) {
        // Ámbito resuelto en servidor (receta de 3 estados; NUNCA se decide por ambito==null):
        //   1) !ambitoCargado            → CARGANDO: espera (anti-blip), no redirijas.
        //   2) ambitoError               → NO LO SÉ: deja entrar para que la página muestre
        //      aviso+reintento (el gate del backend es la barrera real; no redirigir ni tirar
        //      sesión por un hipo de red). La bandeja se cierra a nivel de contenido, no de ruta.
        //   3) LISTO                     → decide el booleano del servidor `ambito.puedeGestionar`
        //      (mismo flag que el gate; imposible desalinear).
        if (!ambitoCargado) return <LoaderSpinner />;
        if (ambitoError || ambito?.puedeGestionar) {
            return <Outlet />;
        }
    }

    // Anti-blip: esperar los permisos del backend antes de decidir por
    // permiso fino. Si `cargando` ya bajó (JWT decodificado) pero `permisos`
    // aún no llegó, sin esta espera un gestor no-SA con la clave caería al
    // Navigate to="/login" y volvería al recargar permisos. El flag
    // `permisosCargados` lo expone el provider desde Capa 1 (anti-parpadeo).
    if ((requiredPermission || requiredPermissionPrefix) && !permisosCargados) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh'
            }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    // Permiso individual o por rol asignado dinámicamente
    if (requiredPermission && permisos && permisos.includes(requiredPermission)) {
        return <Outlet />;
    }

    // Permiso por PREFIJO: pasa si tiene CUALQUIER permiso que empiece por
    // el prefijo (ej. requiredPermissionPrefix="inventario." → cualquiera
    // de inventario.dashboard.view, inventario.activos.view, etc.).
    // Aditivo: no afecta a las rutas que no usan esta prop.
    if (requiredPermissionPrefix && permisos && permisos.some(p => p.startsWith(requiredPermissionPrefix))) {
        return <Outlet />;
    }

    // Eje de PARTICIPACIÓN (Citas Técnicas): pasa si el usuario participa en
    // campañas aunque no tenga el permiso fino — mismo OR que `accesoMisCitas`
    // del backend (participación O permiso). Se delega a un componente hijo
    // para NO disparar /mi-disponibilidad en las rutas que no usan estos flags.
    if (allowIfParticipante || allowIfTecnico) {
        return (
            <ParticipacionGate
                hasSession={!!persona}
                allowIfParticipante={allowIfParticipante}
                allowIfTecnico={allowIfTecnico}
                location={location}
            />
        );
    }

    // Denegado. Hay que distinguir SIN SESIÓN de AUTENTICADO-SIN-PERMISO:
    // mandar a /login a un usuario ya autenticado sería tóxico con el retorno
    // activo (guardaría destino, el "login" no haría nada por haber sesión, y
    // el retorno lo rebotaría al sitio vedado → bucle o login espurio).
    if (!persona) {
        // Sin sesión → a login, preservando el destino (sessionStorage, que
        // sobrevive a reload/reset, + state.from como respaldo).
        guardarDestinoRetorno(destinoDesdeLocationState(location));
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    // Autenticado pero sin permiso → a panel-inicio, SIN guardar destino (no es
    // un problema de sesión). En una denegación del guard NO hay llamada a la
    // API (se decide en cliente), así que el toast de handleAuthError no salta:
    // pasamos el aviso por el estado de navegación para que /panel-inicio lo
    // muestre y el usuario no aterrice sin explicación.
    return <Navigate to="/panel-inicio" replace state={{ avisoAcceso: AVISO_SIN_ACCESO }} />;
};

// Gate del eje de participación. Aislado en su propio componente para que el
// hook useIntervencionesDisponibilidad (que llama a GET /mi-disponibilidad)
// solo se ejecute en las rutas que declaran allowIfParticipante/allowIfTecnico.
function ParticipacionGate({ hasSession, allowIfParticipante, allowIfTecnico, location }) {
    // El GUARD autoriza por HISTÓRICO, no por activo: quien participó alguna vez
    // (campaña no cancelada) puede entrar a ver su histórico en /mis-campanas,
    // aunque ya no tenga nada activo y por tanto NO vea el atajo/menú (que van
    // por el eje activo). Coincide con la barrera real del backend (accesoMisCitas).
    const { esUsuarioHistorico, esTecnicoHistorico, loading, error } = useIntervencionesDisponibilidad();
    // Anti-blip: esperar a que resuelva la disponibilidad antes de redirigir.
    if (loading) return <LoaderSpinner />;
    if (allowIfParticipante && esUsuarioHistorico) return <Outlet />;
    if (allowIfTecnico && esTecnicoHistorico) return <Outlet />;
    // Fail-open SOLO con sesión válida: si /mi-disponibilidad falló de forma
    // transitoria no fabricamos un "no tienes acceso" — el guard es UX, la
    // barrera real es `accesoMisCitas` en backend; dejamos pasar y que la API
    // responda (contenido o 403 legítimo). PERO sin sesión el 401 de
    // /mi-disponibilidad NO es transitorio: hay que ir a login preservando el
    // destino (imprescindible para la cadena /mis-citas → destino → login).
    if (error && hasSession) return <Outlet />;
    // Mismo criterio que el guard principal: sin sesión → login con destino;
    // autenticado pero sin participación → panel, sin guardar destino (mandarlo
    // a /login teniendo sesión lo rebotaría en bucle).
    if (!hasSession) {
        guardarDestinoRetorno(destinoDesdeLocationState(location));
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <Navigate to="/panel-inicio" replace state={{ avisoAcceso: AVISO_SIN_ACCESO }} />;
}

export default ProtectedRoute;
